import React, { useMemo } from 'react';
import styled from 'styled-components';
import { Collapse, CollapseProps } from 'antd';
import { MinusOutlined, PlusOutlined } from '@ant-design/icons';
import { Link as RouterLink } from 'react-router-dom';

type FaqItem = {
  key: string;
  question: string;
  answer: React.ReactNode;
};

const Col = styled.div`
  width: 100%;
  margin-top: 32px;
`;

const Title = styled.h1`
  margin: 0 0 18px;
  font-size: 44px;
  line-height: 1.12;
  font-weight: 800;
  letter-spacing: -0.02em;

  @media (max-width: 767px) {
    font-size: 34px;
  }
`;

const StyledCollapse = styled(Collapse)`
  background: transparent;

  .ant-collapse-item {
    background: #ffffff;
    border: 1px solid rgba(15, 23, 42, 0.12);
    border-radius: 14px;
    overflow: hidden;
    margin-bottom: 12px;

    box-shadow: 0 12px 28px rgba(0, 0, 0, 0.06),
    0 2px 8px rgba(0, 0, 0, 0.04);
  }

  .ant-collapse-header {
    padding: 14px 16px !important;
    align-items: center !important;
  }

  .ant-collapse-expand-icon {
    margin-inline-end: 10px !important;
  }

  .ant-collapse-content {
    border-top: 1px solid rgba(15, 23, 42, 0.10);
  }

  .ant-collapse-content-box {
    padding: 14px 16px 16px !important;
  }

  /* hover like your theme */

  .ant-collapse-item:not(.ant-collapse-item-active) .ant-collapse-header:hover {
    background: rgba(177, 0, 42, 0.06);
  }
`;

const Question = styled.span`
  font-size: 16px;
  font-weight: 800;
  line-height: 1.35;
  color: rgba(11, 18, 32, 0.95);
`;

const AnswerWrap = styled.div``;

const P = styled.p`
  margin: 0 0 12px;
  font-size: 16px;
  line-height: 1.75;
  color: rgba(11, 18, 32, 0.86);

  &:last-child {
    margin-bottom: 0;
  }
`;

const Ol = styled.ol`
  margin: 8px 0 12px 18px;
  padding: 0;

  li {
    margin: 0 0 10px;
    line-height: 1.75;
    color: rgba(11, 18, 32, 0.86);
  }

  li:last-child {
    margin-bottom: 0;
  }
`;

const Strong = styled.strong`
  font-weight: 900;
`;

const InlineLink = styled(RouterLink)`
  color: #b1002a;
  font-weight: 800;

  text-decoration: underline;
  text-decoration-thickness: 2px;
  text-underline-offset: 3px;

  &:hover {
    opacity: 0.85;
  }
`;

const ExternalLink = styled.a`
  color: #b1002a;
  font-weight: 800;

  text-decoration: underline;
  text-decoration-thickness: 2px;
  text-underline-offset: 3px;

  &:hover {
    opacity: 0.85;
  }
`;

export const GisFaq: React.FC = () => {
  const items = useMemo<FaqItem[]>(
    () => [
      {
        key: '1',
        question: 'What does GIS stand for?',
        answer: <P>GIS stands for Guarantee and Inspection Service.</P>,
      },
      {
        key: '2',
        question: 'Who operates the GIS program?',
        answer: <P>AWMAC and the regional AWMAC Chapters administer the GIS program on a per-project basis.</P>,
      },
      {
        key: '3',
        question: 'What do I have to do?',
        answer: (
                    <>
                        <P>Follow these four steps:</P>
                        <Ol>
                            <li>Specify the Guarantee and Inspection Service.</li>
                            <li>
                                Specify the AWMAC grade required. Grades are Custom and Premium. Both grades may occur
                                within a single
                                project. If you do not specify a grade, Custom grade is the default. The Manufacturer
                                will assure that
                                the materials and processes will comply with AWMAC’S STANDARDS.
                            </li>
                            <li>
                                The Manufacturer will email the project details (project name, general contractor,
                                design professional,
                                tender date, etc.) to the regional AWMAC Chapter. The cost of the GIS will be part of
                                the tendered lump
                                sum, not a separate line item. Once the regional Chapter is notified of the GIS project,
                                tracking begins,
                                inspections and reports are completed, and follow-up on compliance is conducted.
                            </li>
                            <li>
                                Once the supply and installation of the completed architectural woodwork complies with
                                AWMAC’s Standards,
                                a two-year guarantee on the architectural woodwork will be issued to the AWMAC
                                Manufacturer Member.
                            </li>
                        </Ol>
                    </>
        ),
      },
      {
        key: '4',
        question: 'When does GIS come into effect?',
        answer: (
                    <P>
                        The GIS program comes into effect as soon as the tender documents specify the requirements for
                        the Guarantee
                        and Inspection Service.
                    </P>
        ),
      },
      {
        key: '5',
        question:
                    'What if the architectural woodwork manufacturer is not an AWMAC member? Can we still get the GIS on this project?',
        answer: (
                    <P>
                        The GIS is available to both members and non-members; however, only AWMAC members in good
                        standing will
                        receive the guarantee. AWMAC suggests that the design professional asks non-members for a
                        two-year
                        maintenance bond. See <InlineLink to="/gis/gis-wording">Wording for Specifying GIS</InlineLink>.
                    </P>
        ),
      },
      {
        key: '6',
        question: 'What is involved in GIS?',
        answer: (
                    <>
                        <P>
                            Once specified, the GIS is a three-step process with a physical inspection and written
                            report issued for
                            each step:
                        </P>
                        <Ol>
                            <li>
                                <Strong>Shop Drawing Review</Strong> – This review is in addition to the usual review by
                                the Architect.
                                The AWMAC Certified Inspector determines whether or not the methods and materials
                                indicated in the shop
                                drawings submitted by the Manufacturer conform to the specified AWMAC’s STANDARDS and
                                may also indicate
                                deviation from architectural drawings and specifications.
                            </li>
                            <li>
                                <Strong>Sample Unit Inspection</Strong> - If specified, a sample unit is inspected for
                                compliance with
                                AWMAC’s STANDARDS in the manufacturing facility or on-site.
                            </li>
                            <li>
                                <Strong>Final Site Inspection</Strong> - Upon project completion, the architectural
                                woodwork is
                                inspected on-site for compliance with AWMAC’s STANDARDS. If it is a large project, more
                                than one
                                inspection may be necessary. In addition, if a design professional is interested, they
                                can contact the
                                Chapter office in advance of tender for a <Strong>Pre-tender Review</Strong>. An AWMAC
                                Certified
                                Inspector will review the specifications and architectural drawings to flag any errors,
                                omissions or
                                contradictions confusing tender, manufacturing or installation
                            </li>
                        </Ol>
                    </>
        ),
      },
      {
        key: '7',
        question: 'Who pays the inspection cost?',
        answer: (
                    <P>
                        Based on the architectural woodwork contract, the inspection cost is borne by the Manufacturer
                        but is part of
                        the project tender.
                    </P>
        ),
      },
      {
        key: '8',
        question: 'How are AWMAC GIS Inspectors certified?',
        answer: (
                    <>
                        <P>
                            AWMAC GIS Inspectors must have significant experience and knowledge in building
                            construction, specifically
                            architectural woodwork.
                        </P>
                        <P>
                            Inspectors must pass AWMAC’s Inspection Certification Exam, achieving a minimum grade of
                            95%. Upon
                            completion of the exam, the inspector will complete a set of four GIS inspections under the
                            supervision of
                            an AWMAC GIS Inspector.
                        </P>
                        <P>Once the inspector-in-training has completed four inspections, they will become a certified
                            AWMAC GIS Inspector.</P>
                    </>
        ),
      },
      {
        key: '9',
        question:
                    'What assurance do I have that an AWMAC manufacturer member has supplied and/or installed the architectural woodwork in accordance with AWMAC’s STANDARDS?',
        answer: (
                    <P>
                        AWMAC, through its Chapter, will issue a two-year Certificate of Guarantee on projects which
                        have passed the
                        final GIS inspection. The final inspection will not be passed until all deficient items have
                        been rectified
                        and the project is brought up to the standards for the grade specified on the project.
                    </P>
        ),
      },
      {
        key: '10',
        question: 'What happens if the architectural woodwork proves to be defective during the two-year guarantee?',
        answer: (
                    <P>
                        Subject to exclusions, if any, outlined in the Guarantee, the AWMAC architectural woodwork
                        manufacturer who
                        signed as the first guarantor shall replace all defective architectural woodwork on the project,
                        provided
                        that the defects in the architectural woodwork are caused wholly as a result of faulty
                        workmanship or
                        defective material supplied by AWMAC’s architectural woodwork manufacturer.
                    </P>
        ),
      },
      {
        key: '11',
        question: 'What AWMAC grade should I specify?',
        answer: (
                    <P>
                        Please see the{' '}
                        <ExternalLink href="https://awmac.com/gis/awmacs-grades/" target="_blank"
                                      rel="noopener noreferrer">
                            AWMAC&apos;s Grades page
                        </ExternalLink>
                        .
                    </P>
        ),
      },
      {
        key: '12',
        question:
                    'What precautions have AWMAC considered if the AWMAC architectural woodwork manufacturer who signed as the first guarantor cannot remedy a defect?',
        answer: (
                    <>
                        <P>
                            AWMAC will not issue a Certificate of Guarantee unless the supply and installation of the
                            completed
                            architectural woodwork on a project comply with AWMAC’s Standards.
                        </P>
                        <P>The regional AWMAC Chapter that has performed the inspections becomes the second
                            guarantor.</P>
                        <P>
                            If the manufacturer cannot, for whatever reason, address these issues, then the AWMAC
                            Chapter office will
                            hire another AWMAC manufacturer member to complete the work. The Chapter office will bear
                            the cost.
                        </P>
                        <P>
                            AWMAC becomes the third guarantor in the remote chance that the project's supply and
                            installation of
                            architectural woodwork become defective during the two-year guarantee period.
                        </P>
                    </>
        ),
      },
    ],
    [],
  );

  const renderExpandIcon: NonNullable<CollapseProps['expandIcon']> = (panelProps) => (panelProps.isActive ? <MinusOutlined /> : <PlusOutlined />);

  return (
        <Col>
            <Title>GIS FAQ</Title>
            <StyledCollapse
                accordion
                bordered={false}
                defaultActiveKey={['1']}
                expandIconPosition="start"
                expandIcon={renderExpandIcon}
                items={items.map((it) => ({
                  key: it.key,
                  label: <Question>{it.question}</Question>,
                  children: <AnswerWrap>{it.answer}</AnswerWrap>,
                }))}
            />
        </Col>
  );
};
